import React from 'react'
import { useMutation } from '@tanstack/react-query'
import { useAlert } from 'contexts/AlertContext'
import { useAuthenticatedUser } from 'contexts/UserContext'

import { currentQueryService } from 'utils/api/queries'
import type { UpdateSchemaBody } from 'utils/api/queries'
import { convertIntervalDTO } from 'utils/helpers/convertIntervalDTO'
import { useSchemaState } from 'contexts/SchemaContext'
import { useSingleSelectedCompany } from 'utils/hooks/useSingleSelectedCompany'
import type { Schema } from 'models/schema'

const useUpdateSchema = () => {
  const { authenticatedUser, displayedEmail, isLoginB2C } = useAuthenticatedUser()
  const { schema, intervals } = useSchemaState()
  const { displayAlert } = useAlert()
  const { hasSingleSelection, selectedCompany } = useSingleSelectedCompany()
  const userId = authenticatedUser.clientPrincipal?.userId as string
  const companyId = hasSingleSelection ? selectedCompany?.id || '' : ''

  const isGuarded = !hasSingleSelection || !companyId

  const processedIntervals = intervals.map((interval) => {
    const stripped = convertIntervalDTO(interval)
    return stripped
  })

  const mutationFn = React.useCallback(
    async (schemaOverride?: Schema) => {
      if (isGuarded) {
        throw new Error('Single company selection required before updating schema')
      }

      const updateBody: UpdateSchemaBody = {
        userId,
        companyId,
        type: isLoginB2C ? 'ExternalUser' : 'AdminUser',
        tableMappings: (schemaOverride ?? schema).tableMappings,
        intervals: processedIntervals,
        // TODO: get email for B2C/B2B
        requestEmail: displayedEmail,
      }

      return currentQueryService.updateSchema(updateBody)
    },
    [companyId, displayedEmail, isGuarded, isLoginB2C, processedIntervals, schema, userId],
  )

  const query = useMutation<string, Error, Schema | undefined>({
    mutationKey: ['useUpdateSchema', schema.id],
    mutationFn,
  })

  React.useEffect(() => {
    if (query.isError) {
      displayAlert(query.error?.message || 'Erreur pendant la mise à jour du schema', 'error')
    }
    if (query.isSuccess) {
      displayAlert('Préférences mises à jour avec succès', 'success')
    }
  }, [displayAlert, query.error?.message, query.isError, query.isSuccess])

  return {
    ...query,
    mutate: (schemaOverride?: Schema) => query.mutate(schemaOverride),
    mutateAsync: (schemaOverride?: Schema) => query.mutateAsync(schemaOverride),
    requiresSingleSelection: isGuarded,
  }
}

export { useUpdateSchema }
